import { useState, useEffect, useCallback, useRef } from 'react'
import { useSsoToken } from './useSsoToken'
import { getClaimTasks, updateClaimTask, ApiValidationError } from '../services/claimsApi'
import { getApiErrorMessage } from '../utils/errorMessages'
import type { ApiSlice } from './useClaimDetails'
import type { ClaimTask, TaskHoldValues } from '../types/task'

// How the MC1 on-hold proc words the refusals a user can act on, swapped for plain wording.
// Matched on the text rather than the Msg number, which differs between procs. Anything not
// listed is shown as the proc worded it.
const HOLD_REFUSAL_MESSAGES: [RegExp, string][] = [
  [/not open/i, 'This task is no longer open, so it cannot be put on hold.'],
  [/already on-?hold/i, 'This task is already on hold.'],
  [/later than today/i, 'The On Hold Until date must be later than today.'],
  [/not assigned to this claim/i, 'This task does not belong to this claim.'],
]

const toHoldRefusalMessage = (message: string): string =>
  HOLD_REFUSAL_MESSAGES.find(([pattern]) => pattern.test(message))?.[1] ?? message

// A rule the proc enforced (400) rather than a transport failure: retrying the same values
// can't succeed, so the reason is shown instead of the generic message.
function getHoldErrorMessage(err: unknown): string {
  if (err instanceof ApiValidationError) return err.messages.map(toHoldRefusalMessage).join(' ')
  return getApiErrorMessage(err, 'Failed to update task.')
}

const initialSlice = <T>(): ApiSlice<T> => ({ data: null, loading: false, error: null })
const loadingSlice = <T>(): ApiSlice<T> => ({ data: null, loading: true, error: null })

// Fetches the connected claim's open tasks for the Tasks tab (MSD-682).
export function useClaimTasks(claimNumber: string | null) {
  const { getToken } = useSsoToken()

  const [tasks, setTasks] = useState<ApiSlice<ClaimTask[]>>(initialSlice)
  const [holdingTaskId, setHoldingTaskId] = useState<string | null>(null)
  // Kept with its task id so the message shows only on the card that failed.
  const [holdError, setHoldError] = useState<{ taskId: string; message: string } | null>(null)

  // Each fetch gets an id; a response only lands if its fetch is still the latest,
  // so a late response can't clobber state after a claim swap, retry, or unmount.
  const fetchIdRef = useRef(0)

  // `silent` refreshes the list in the background: the current tasks stay on screen
  // instead of being replaced by the loading skeletons. That matters after an update,
  // because blanking the list unmounts every card and throws away its open state.
  const fetchTasks = useCallback(
    async (silent = false) => {
      const fetchId = ++fetchIdRef.current
      const isStale = () => fetchIdRef.current !== fetchId

      if (!claimNumber) {
        setTasks(initialSlice)
        return
      }

      if (!silent) setTasks(loadingSlice)

      try {
        const token = await getToken()
        const data = await getClaimTasks(claimNumber, token)
        if (!isStale()) setTasks({ data, loading: false, error: null })
      } catch (err: unknown) {
        if (isStale()) return
        console.error('[ClaimTasks] Failed to load tasks:', err)
        // A failed background refresh keeps the list that is already on screen; the
        // update itself succeeded, so wiping the tab would be the wrong story.
        if (silent) return
        setTasks({ data: null, loading: false, error: getApiErrorMessage(err, 'Failed to load tasks.') })
      }
    },
    [claimNumber, getToken]
  )

  // Takes no arguments, so it is safe to hand straight to an onClick.
  const retry = useCallback(() => fetchTasks(), [fetchTasks])

  useEffect(() => {
    fetchTasks()
    return () => {
      fetchIdRef.current += 1
    }
    // Refetch only on claim change; fetchTasks is stable (memoized on claimNumber).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimNumber])

  // Puts a task on hold in one PATCH — On Hold Until date and description travel
  // together. The backend derives the Hold status itself, so the request carries no
  // status id. Resolves true on success so the card only closes when saved.
  const holdTask = useCallback(
    async (taskId: string, values: TaskHoldValues): Promise<boolean> => {
      if (!claimNumber) return false

      const numericTaskId = Number(taskId)
      if (!Number.isInteger(numericTaskId)) {
        console.error('[ClaimTasks] Task id is not numeric, cannot update:', taskId)
        setHoldError({ taskId, message: 'This task cannot be updated.' })
        return false
      }

      setHoldingTaskId(taskId)
      setHoldError(null)

      try {
        const token = await getToken()
        await updateClaimTask(
          claimNumber,
          numericTaskId,
          {
            onHoldUntilDate: new Date(values.onHoldUntilDate),
            holdDescription: values.holdDescription,
          },
          token
        )
        // Background refresh so the list does not flash back to skeletons.
        await fetchTasks(true)
        return true
      } catch (err: unknown) {
        console.error('[ClaimTasks] Failed to put task on hold:', err)
        setHoldError({ taskId, message: getHoldErrorMessage(err) })
        return false
      } finally {
        setHoldingTaskId(null)
      }
    },
    [claimNumber, getToken, fetchTasks]
  )

  return {
    tasks,
    retry,
    holdTask,
    holdingTaskId,
    holdError,
  }
}
